import { useUser } from "@/composables/user/user";
import { createError404Link } from "@/router/linkFactories";
import type { RouteName } from "@/router/routeNameEnum";
import type { Right } from "@/services/user/userTypes";
import { checkUserOnInitialPageLoad } from "@/services/user/auth/login/helpers";
import { type NavigationGuardNext, type RouteLocationNormalized } from "vue-router";

/**
 * Registered as a global `router.beforeEach` (see registerGuards.ts), scoped to routeName,
 * rather than as a `beforeEnter` on the route itself. A `beforeEnter` runs as part of the
 * router's initial navigation, which fires before the Nuxt Pinia plugin has finished installing
 * Pinia on the app — calling useUser() at that point throws "getActivePinia() was called but
 * there was no active Pinia". router.beforeEach guards are registered from a `.client` plugin
 * that always runs after Pinia is installed, so they don't hit this ordering issue.
 */
export const checkUserHasRight = (routeName: RouteName, requestedRight: Right) => {
  return async (toRoute: RouteLocationNormalized, _: RouteLocationNormalized, next: NavigationGuardNext) => {
    if (toRoute.name !== routeName) {
      return next();
    }

    await checkUserOnInitialPageLoad();
    const { hasRight } = useUser();
    if (!hasRight(requestedRight)) {
      next(createError404Link());
    } else {
      next();
    }
  };
};
